import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationPageSize,
} from "@/components/ui/pagination";
import {
  Bell,
  Plus,
  Target,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Calendar,
  Flag,
  BarChart3,
  Pencil,
  Trash2,
  X,
  FolderOpen,
  Save,
  Loader2,
  Search
} from 'lucide-react';
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import NoData from "@/components/ui/NoData";

import {
  getGoals,
  createGoal,
  updateGoal,
  updateGoalProgress,
  addKeyResult,
  deleteKeyResult,
  deleteGoal,
  sendGoalReminder,
} from '@/services/goalService';

import { getEmployees } from '@/services/adminService';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import { usePermission } from '@/contexts/RoleAccessContext';
import { useSubscription } from "@/contexts/SubscriptionContext";

const statusConfig = {
  "Completed": { color: "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800", dot: "bg-green-500" },
  "On Track": { color: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800", dot: "bg-blue-500" },
  "At Risk": { color: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800", dot: "bg-amber-500" },
  "Behind": { color: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800", dot: "bg-red-500" },
};

const priorityConfig = {
  "Critical": "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
  "High": "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
  "Medium": "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
  "Low": "bg-muted text-muted-foreground border-muted-foreground/20 dark:border-muted-foreground/20",
};

const GoalTracking = ({ isCreateOpen: externalIsCreateOpen, onOpenChange: externalOnOpenChange }) => {
  const { can } = usePermission();
  
  const canCreateGoal = can("goals_okrs", "create");
  const canEditGoal = can("goals_okrs", "edit");
  const canDeleteGoal = can("goals_okrs", "delete");
  
  // Departments are a paid feature — the column is hidden, but the value
  // already stored on each goal is left untouched.
  const { hasFeature } = useSubscription();
  const canUseDepartments = hasFeature("department_mapping");

  const [internalIsCreateOpen, setInternalIsCreateOpen] = useState(false);
  const isCreateOpen = externalIsCreateOpen ?? internalIsCreateOpen;
  const setIsCreateOpen = externalOnOpenChange ?? setInternalIsCreateOpen;
  const [goals, setGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGoal, setExpandedGoal] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [reminderSending, setReminderSending] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1); // reset to first page when page size changes
  };

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const response = await getEmployees(undefined, undefined, {
                  statuses: ["active", "exit_scheduled"],
                  scope: 'performance',
                });
        const empData = response.data || response;
        setEmployees(empData);
      } catch (error) {
        console.error("Failed to load employees:", error);
      }
    };
    loadEmployees();
  }, []);

  // Load goals from API
  useEffect(() => {
    const loadGoals = async () => {
      try {
        setLoadingGoals(true);
        const response = await getGoals();
        const goalsData = response.data || response;
        setGoals(goalsData);
        const uniqueProjects = [...new Set(goalsData.map(g => g.project).filter(Boolean))];
        setProjects(uniqueProjects);
      } catch (error) {
        toast.error("Failed to load goals");
        console.error(error);
      } finally {
        setLoadingGoals(false);
      }
    };
    loadGoals();
  }, []);

  // Dialogs
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddKROpen, setIsAddKROpen] = useState(false);

  // Form state
  const [editingGoal, setEditingGoal] = useState(null);
  const [progressEdits, setProgressEdits] = useState({});
  const [initialProgressEdits, setInitialProgressEdits] = useState({});
  const [initialEditGoal, setInitialEditGoal] = useState(null);
  const [newKRText, setNewKRText] = useState("");

  // Create Goal form state
  const [newGoal, setNewGoal] = useState({
    title: "", description: "", owner: "", priority: "", type: "", project: "", startDate: new Date().toISOString().split('T')[0], dueDate: "",
    keyResults: [{ id: 'new_kr1', text: "", progress: 0 }],
  });
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");

  const filteredGoals = goals.filter(g => {
    const statusMatch = filterStatus === "all" || g.status === filterStatus;
    const projectMatch = filterProject === "all" || (g.project || "Unassigned") === filterProject;

    // Search filter
    const searchLower = searchQuery.toLowerCase();
    const searchMatch = !searchQuery ||
      (g.title && g.title.toLowerCase().includes(searchLower)) ||
      (g.employee_name && g.employee_name.toLowerCase().includes(searchLower)) ||
      (g.owner && g.owner.toLowerCase().includes(searchLower)) ||
      (g.department && g.department.toLowerCase().includes(searchLower)) ||
      (g.project && g.project.toLowerCase().includes(searchLower));

    return statusMatch && projectMatch && searchMatch;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterProject, searchQuery]);

  // Calculate pagination
  const totalGoalsPages = Math.ceil(filteredGoals.length / pageSize);
  const paginatedGoals = filteredGoals.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const stats = {
    total: goals.length,
    onTrack: goals.filter(g => g.status === "On Track").length,
    atRisk: goals.filter(g => g.status === "At Risk" || g.status === "Behind").length,
    completed: goals.filter(g => g.status === "Completed").length,
    avgProgress: goals.length ? Math.round(goals.reduce((s, g) => s + g.progress, 0) / goals.length) : 0,
  };

  // --- Create Project ---
  const handleCreateProject = () => {
    if (!newProjectName.trim()) return;
    if (projects.includes(newProjectName.trim())) { toast.error("Project already exists"); return; }
    setProjects(prev => [...prev, newProjectName.trim()]);
    setNewGoal(p => ({ ...p, project: newProjectName.trim() }));
    setNewProjectName("");
    setIsCreatingProject(false);
  };

  const addNewKRField = () => {
    setNewGoal(prev => ({
      ...prev,
      keyResults: [...prev.keyResults, { id: 'new_kr' + (prev.keyResults.length + 1), text: "", progress: 0 }]
    }));
  };

  const updateNewKR = (index, text) => {
    setNewGoal(prev => ({
      ...prev,
      keyResults: prev.keyResults.map((kr, i) => i === index ? { ...kr, text } : kr)
    }));
  };

  const removeNewKR = (index) => {
    if (newGoal.keyResults.length <= 1) return;
    setNewGoal(prev => ({ ...prev, keyResults: prev.keyResults.filter((_, i) => i !== index) }));
  };

  const handleCreateGoal = async () => {
    if (!newGoal.title.trim()) { toast.error("Goal title is required"); return; }

    try {
      const selectedEmployee = employees.find(emp =>
        emp.user_id?.name === newGoal.owner ||
        emp.name === newGoal.owner
      );

      if (!selectedEmployee) {
        toast.error("Please select a valid employee");
        return;
      }

    if (!newGoal.priority) { toast.error("Please select priority"); return; }
    if (!newGoal.type) { toast.error("Please select goal type"); return; }
    if (!newGoal.project) { toast.error("Please select project"); return; }
    if (!newGoal.startDate) { toast.error("Start date is required"); return; }
    if (!newGoal.dueDate) { toast.error("Due date is required"); return; }
    
    if (new Date(newGoal.dueDate) <= new Date(newGoal.startDate)) {
      toast.error("Due date must be after start date");
      return;
    }
  
    const krs = newGoal.keyResults.filter(kr => kr.text.trim());
    if (krs.length === 0) { toast.error("Add at least one key result"); return; }

      const created = await createGoal({
        title: newGoal.title,
        description: newGoal.description,
        employeeId: selectedEmployee.employee_id,
        department: selectedEmployee.department || "General",
        designation: selectedEmployee.designation || "",
        type: newGoal.type || "OKR",
        priority: newGoal.priority || "Medium",
        startDate: newGoal.startDate || new Date().toISOString().split('T')[0],
        dueDate: newGoal.dueDate || "",
        project: newGoal.project || "Unassigned",
        keyResults: krs.map((kr, i) => ({
          id: `kr-${Date.now()}-${i}`,
          title: kr.text,
          current: 0,
          target: 100,
          unit: '%'
        })),
      });

      const goalData = created.data || created;
      setGoals([goalData, ...goals]);

      setNewGoal({
        title: "", description: "", owner: "", priority: "",
        type: "", project: "", startDate: "", dueDate: "",
        keyResults: [{ id: 'new_kr1', text: "", progress: 0 }]
      });
      setIsCreateOpen(false);
      toast.success("Goal created", { description: `"${goalData.title}" saved.` });
    } catch (err) {
      console.error("Create goal error:", err);
      console.error("Error response:", err.response?.data);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to create goal");
    }
  };

  // --- Update Progress ---
  const openProgressDialog = (goal) => {
    if (goal.status === "Completed") {
      toast.info("Cannot update progress for completed goals");
      return;
    }
    const edits = {};
    goal.keyResults.forEach(kr => { edits[kr.id] = kr.progress ?? kr.current ?? 0; });
    setProgressEdits(edits);
    setInitialProgressEdits({ ...edits });
    setEditingGoal(goal);
    setIsProgressOpen(true);
  };

  const handleSaveProgress = async () => {
    // Check if any progress values have changed
    const hasChanges = editingGoal?.keyResults.some(kr => {
      const currentValue = progressEdits[kr.id] ?? kr.current ?? kr.progress ?? 0;
      const initialValue = initialProgressEdits[kr.id];
      return currentValue !== initialValue;
    });

    if (!hasChanges) {
      toast.info("No changes to save");
      return;
    }

    try {
      const updatedKRs = editingGoal.keyResults.map(kr => ({
        id: kr.id,
        title: kr.title || kr.text,
        current: progressEdits[kr.id] ?? kr.current ?? kr.progress ?? 0,
        target: kr.target || 100,
        unit: kr.unit || '%'
      }));

      const response = await updateGoalProgress(editingGoal._id || editingGoal.id, updatedKRs);
      const updated = response.data || response;

      setGoals(goals.map(g => (g._id || g.id) === updated._id ? updated : g));
      setIsProgressOpen(false);
      toast.success("Progress updated");
    } catch (err) {
      console.error("Save progress error:", err);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to save progress");
    }
  };

  // --- Edit Goal ---
  const openEditDialog = (goal) => {
    if (goal.status === "Completed") {
      toast.info("Cannot edit completed goals");
      return;
    }
    const goalCopy = { ...goal };
    setEditingGoal(goalCopy);
    setInitialEditGoal(JSON.parse(JSON.stringify(goalCopy)));
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    // Check if any values have changed
    const hasChanges = JSON.stringify(editingGoal) !== JSON.stringify(initialEditGoal);

    if (!hasChanges) {
      toast.info("No changes to save");
      return;
    }

    const krs = editingGoal.keyResults.filter(kr => (kr.text || kr.title || '').trim());
    if (krs.length === 0) { toast.error("At least one key result is required"); return; }
    if (!editingGoal.startDate) { toast.error("Start date is required"); return; }
    if (!editingGoal.dueDate) { toast.error("Due date is required"); return; }
    if (new Date(editingGoal.dueDate) <= new Date(editingGoal.startDate)) {
      toast.error("Due date must be after start date");
      return;
    }

    try {
      const formattedKRs = krs.map(kr => ({
        id: kr.id,
        title: kr.title || kr.text,
        current: kr.progress ?? kr.current ?? 0,
        target: kr.target || 100,
        unit: kr.unit || '%'
      }));

      const response = await updateGoal(editingGoal._id || editingGoal.id, {
        title: editingGoal.title,
        description: editingGoal.description,
        priority: editingGoal.priority,
        type: editingGoal.type,
        project: editingGoal.project,
        dueDate: editingGoal.dueDate,
        startDate: editingGoal.startDate,
        employeeId: editingGoal.employee_id,
        keyResults: formattedKRs,
      });

      const updated = response.data || response;
      setGoals(goals.map(g => (g._id || g.id) === updated._id ? updated : g));
      setIsEditOpen(false);
      toast.success("Goal updated");
    } catch (err) {
      console.error("Update goal error:", err);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to update goal");
    }
  };

  // --- Delete Goal ---
  const openDeleteDialog = (goal) => {
    setEditingGoal(goal);
    setIsDeleteOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteGoal(editingGoal._id || editingGoal.id);
      setGoals(goals.filter(g => (g._id || g.id) !== editingGoal._id));
      setIsDeleteOpen(false);
      if (expandedGoal === (editingGoal._id || editingGoal.id)) setExpandedGoal(null);
      toast.success("Goal deleted");
    } catch (err) {
      console.error("Delete goal error:", err);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to delete goal");
    }
  };

  // --- Add Key Result ---
  const openAddKRDialog = (goal) => {
    if (goal.status === "Completed") {
      toast.info("Cannot add key results to completed goals");
      return;
    }
    setEditingGoal(goal);
    setNewKRText("");
    setIsAddKROpen(true);
  };

  const handleAddKR = async () => {
    if (!newKRText.trim()) { toast.error("Key result text is required"); return; }

    try {
      const response = await addKeyResult(editingGoal._id || editingGoal.id, {
        title: newKRText,
        current: 0,
        target: 100,
        unit: '%'
      });

      const updated = response.data || response;
      setGoals(goals.map(g => (g._id || g.id) === updated._id ? updated : g));
      setIsAddKROpen(false);
      toast.success("Key result added");
    } catch (err) {
      console.error("Add key result error:", err);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to add key result");
    }
  };

  // --- Delete Key Result ---
  const deleteKR = async (goalId, krId) => {
    try {
      const response = await deleteKeyResult(goalId, krId);
      const updated = response.data || response;
      setGoals(goals.map(g => (g._id || g.id) === updated._id ? updated : g));
      toast.success("Key result removed");
    } catch (err) {
      console.error("Delete key result error:", err);
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to remove key result");
    }
  };

  const handleSendGoalReminder = async () => {
    if (selectedGoals.length === 0) {
      toast.error("Please select at least one goal");
      return;
    }

    setReminderSending(true);
    try {
      const response = await sendGoalReminder(selectedGoals);

      if (response?.data?.sent > 0) {
        if (response?.data?.errors?.length > 0) {
          toast.success(`Reminder sent for ${response.data.sent} goal(s). ${response.data.errors.length} failed.`);
        } else {
          toast.success(`Reminder sent for ${response.data.sent} goal(s)!`);
        }
        setSelectedGoals([]);
      } else {
        toast.info("All selected goals are completed. No reminders were sent.");
      }
    } catch (error) {
      console.error("Error sending goal reminders:", error);
      toast.error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to send reminders"
      );
    } finally {
      setReminderSending(false);
    }
  };

  // --- Toggle expand/collapse for Key Results ---
  // (handled inline in renderGoalRow)

  if (loadingGoals) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading goals...</span>
      </div>
    );
  }

  // --- Render a table row ---
  const renderGoalRow = (goal) => {
    const goalId = goal._id || goal.id;
    const isExpanded = expandedGoal === goalId;
    const sc = statusConfig[goal.status] || statusConfig["Behind"];
    const ownerName = goal.employee_name || goal.owner || "Unknown";
    const isOverdue = goal.dueDate && new Date(goal.dueDate) < new Date() && goal.status !== "Completed";

    return (
      <React.Fragment key={goalId}>
        <tr
          className="group border-b border-muted hover:bg-muted/50 transition-colors cursor-pointer"
          onClick={() => setExpandedGoal(isExpanded ? null : goalId)}
          data-testid={`goal-row-${goalId}`}
        >
          <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selectedGoals.includes(goal._id)}
              onCheckedChange={(c) => setSelectedGoals(
                c ? [...selectedGoals, goal._id] : selectedGoals.filter(id => id !== goal._id)
              )}
            />
          </td>
          <td className="py-3 px-4">
            <h3
              className="font-medium text-foreground text-sm truncate max-w-[260px]"
              title={goal.title}
            >
              {goal.title}
            </h3>
          </td>
          <td className="py-3 px-4 text-sm">{ownerName}</td>
          {canUseDepartments && (
            <td className="py-3 px-4 text-muted-foreground text-xs">{goal.department || "-"}</td>
          )}
          <td className="py-3 px-4">
            <Badge variant="outline" className={`text-[10px] ${priorityConfig[goal.priority] || ""}`}>
              {goal.priority}
            </Badge>
          </td>
          <td className="py-3 px-4">
            <Badge variant="outline" className={`text-[10px] ${sc.color}`}>
              {goal.status}
            </Badge>
          </td>
          <td className="py-3 px-4 text-muted-foreground text-xs">{goal.project || "Unassigned"}</td>
          <td className="py-3 px-4">
            <div className="flex items-center gap-2">
              <Progress value={goal.progress} className="h-2 flex-1" />
              <span className="text-xs font-semibold min-w-[35px]">{goal.progress}%</span>
            </div>
          </td>
          <td className="py-3 px-4 text-xs text-muted-foreground">
            <span className={cn(isOverdue && "text-red-600 font-semibold")}>
              {goal.dueDate ? new Date(goal.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "-"}
            </span>
          </td>
          <td
            className="py-3 px-4 sticky right-0 bg-card z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1 justify-end">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-7 w-7 disabled:opacity-100" onClick={() => openAddKRDialog(goal)} data-testid={`add-kr-${goalId}`} disabled={goal.status === "Completed" || !canCreateGoal}>
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{goal.status === "Completed" ? "Goal is completed" : "Add KR"}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-7 w-7 disabled:opacity-100" onClick={() => openProgressDialog(goal)} data-testid={`update-progress-${goalId}`} disabled={goal.status === "Completed" || !canEditGoal}>
                      <TrendingUp className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{goal.status === "Completed" ? "Goal is completed" : "Update Progress"}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-7 w-7 disabled:opacity-100" onClick={() => openEditDialog(goal)} data-testid={`edit-goal-${goalId}`} disabled={goal.status === "Completed" || !canEditGoal}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{goal.status === "Completed" ? "Goal is completed" : "Edit Goal"}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-100" onClick={() => openDeleteDialog(goal)} data-testid={`delete-goal-${goalId}`} disabled={!canDeleteGoal}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete Goal</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </td>
        </tr>

        {/* Expanded Row for Key Results */}
        {isExpanded && (
          <tr className="border border-muted" data-testid={`goal-expanded-${goalId}`}>
            <td colSpan={canUseDepartments ? 10 : 9} className="bg-muted/20 p-0">
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Flag className="h-3.5 w-3.5 text-primary" /> Key Results ({goal.keyResults.length})
                  </h4>
                  {canEditGoal && (
                  <Button size="sm" variant="outline" className="text-xs gap-1 h-7" onClick={() => openAddKRDialog(goal)} disabled={goal.status === "Completed"}>
                    <Plus className="h-3 w-3" /> Add KR
                  </Button>
                  )}
                </div>
                {goal.keyResults.map((kr) => (
                  <div key={kr.id} className="flex items-center gap-3 group">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-foreground">{kr.text || kr.title}</p>
                        {goal.status !== "Completed" && canEditGoal && (
                          <button
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-500"
                            onClick={() => deleteKR(goalId, kr.id)}
                            data-testid={`delete-kr-${kr.id}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Progress value={kr.progress ?? kr.current ?? 0} className="h-1.5 flex-1" />
                        <span className="text-xs font-semibold min-w-[35px] text-right">{kr.progress ?? kr.current ?? 0}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-6" data-testid="goal-tracking">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
        {[
          { label: "Total Goals", value: stats.total, icon: Target, cls: "text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary" },
          { label: "On Track", value: stats.onTrack, icon: TrendingUp, cls: "text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary" },
          { label: "At Risk", value: stats.atRisk, icon: AlertCircle, cls: "text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary" },
          { label: "Completed", value: stats.completed, icon: CheckCircle2, cls: "text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary" },
          { label: "Avg Progress", value: `${stats.avgProgress}%`, icon: BarChart3, cls: "text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary" },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${stat.cls}`}>
                  <stat.icon className="h-4 w-4" />
                </div>
              </div>
              <h3 className="text-2xl font-bold">{stat.value}</h3>
              <p className="text-xs text-muted-foreground font-medium">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Goals Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className='flex justify-between items-center'>
            <div>
              <CardTitle className="text-base">Goal Tracking</CardTitle>
              <CardDescription>
                Track and manage employee goals and key results.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {selectedGoals.length > 0 && (
                <>
                  <Badge variant="secondary">{selectedGoals.length} selected</Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs gap-1"
                    onClick={handleSendGoalReminder}
                    disabled={reminderSending || !canCreateGoal}
                  >
                    {reminderSending ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Bell className="h-3 w-3" />
                        Send Reminder
                      </>
                    )}
                  </Button>
                </>
              )}
              {/* Filter Row — only when there are records to search/filter */}
              {goals.length > 0 && (
              <div className="flex items-center gap-2">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search goals..."
                    className="pl-8 h-9 w-[250px] text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                      onClick={() => setSearchQuery("")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="On Track">On Track</SelectItem>
                    <SelectItem value="At Risk">At Risk</SelectItem>
                    <SelectItem value="Behind">Behind</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredGoals.length === 0 ? (
            <div className="flex flex-col items-center">
              <NoData
                icon={Target}
                title="No goals found"
                message={
                  filterStatus !== "all" || filterProject !== "all"
                    ? "Try adjusting your filters"
                    : "Create a new goal to get started"
                }
              />
            </div>
          ) : (
          <div className="overflow-x-auto thin-scrollbar">
            <table className="w-full text-sm min-w-[1600px]">
              <thead>
                <tr className="border-y border-gray-200 dark:border-gray-700">
                  <th className="py-3 px-4 text-left w-12 text-gray-700 dark:text-gray-300">
                    <Checkbox
                      checked={selectedGoals.length === paginatedGoals.length && paginatedGoals.length > 0}
                      onCheckedChange={(c) => setSelectedGoals(c ? paginatedGoals.map(g => g._id) : [])}
                    />
                  </th>
                  <th className="py-3 px-4 text-left min-w-[240px] text-gray-700 dark:text-gray-300">Goal</th>
                  <th className="py-3 px-4 text-left min-w-[150px] text-gray-700 dark:text-gray-300">Employee</th>
                  {canUseDepartments && (
                    <th className="py-3 px-4 text-left min-w-[120px] text-gray-700 dark:text-gray-300">Department</th>
                  )}
                  <th className="py-3 px-4 text-left min-w-[110px] text-gray-700 dark:text-gray-300">Priority</th>
                  <th className="py-3 px-4 text-left min-w-[120px] text-gray-700 dark:text-gray-300">Status</th>
                  <th className="py-3 px-4 text-left min-w-[140px] text-gray-700 dark:text-gray-300">Project</th>
                  <th className="py-3 px-4 text-left min-w-[180px] text-gray-700 dark:text-gray-300">Progress</th>
                  <th className="py-3 px-4 text-left min-w-[120px] whitespace-nowrap text-gray-700 dark:text-gray-300">Due Date</th>
                  <th className="py-3 px-4 text-right min-w-[170px] sticky right-0 bg-card z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] text-gray-700 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedGoals.map(renderGoalRow)}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
        {filteredGoals.length > 0 && (
        <CardFooter className="py-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground whitespace-nowrap">
                Showing {Math.min((currentPage - 1) * pageSize + 1, filteredGoals.length)} to {Math.min(currentPage * pageSize, filteredGoals.length)} of {filteredGoals.length} goals
              </div>
              <PaginationPageSize value={pageSize} onValueChange={handlePageSizeChange} />
            </div>
            <Pagination className="!justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    className={currentPage <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                {[1, ...Array.from({ length: Math.min(5, totalGoalsPages) }, (_, i) => {
                  const start = Math.max(2, Math.min(currentPage - 2, totalGoalsPages - 4));
                  return start + i;
                }).filter(p => p > 1 && p < totalGoalsPages), totalGoalsPages]
                  .filter((p, i, arr) => p && arr.indexOf(p) === i)
                  .reduce((acc, page, index, arr) => {
                    if (index === 0) return [page];
                    const prev = arr[index - 1];
                    if (page - prev === 2) return [...acc, prev + 1, page];
                    if (page - prev > 1) return [...acc, '...', page];
                    return [...acc, page];
                  }, [])
                  .map((page, index) => (
                    <PaginationItem key={index}>
                      {page === '...' ? (
                        <span className="px-2 text-muted-foreground">...</span>
                      ) : (
                        <PaginationLink
                          isActive={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setCurrentPage(Math.min(totalGoalsPages, currentPage + 1))}
                    className={currentPage >= totalGoalsPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </CardFooter>
        )}
      </Card>

      {/* ========== CREATE GOAL DIALOG ========== */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => {
        if (!open) {
          setNewGoal({
            title: "",
            description: "",
            owner: "",
            priority: "",
            type: "",
            project: "",
            startDate: "",
            dueDate: "",
            keyResults: [{ id: 'new_kr1', text: "", progress: 0 }]
          });
          setIsCreatingProject(false);
          setNewProjectName("");
          setEmployeeSearchQuery("");
        }
        setIsCreateOpen(open);
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Goal</DialogTitle>
            <DialogDescription>Define an OKR or project goal with measurable key results.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Goal Title *</Label>
              <Input placeholder="e.g., Increase revenue by 20%" value={newGoal.title} onChange={(e) => setNewGoal(p => ({ ...p, title: e.target.value }))} data-testid="new-goal-title" />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea placeholder="Describe the goal and expected outcomes..." value={newGoal.description} onChange={(e) => setNewGoal(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Employee</Label>
                <Select value={newGoal.owner} onValueChange={(v) => {
                  setNewGoal(p => ({ ...p, owner: v }));
                  setEmployeeSearchQuery("");
                }}>
                  <SelectTrigger><SelectValue placeholder="Select employee..." /></SelectTrigger>
                  <SelectContent>
                    <div className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Input
                        placeholder="Search employee..."
                        value={employeeSearchQuery}
                        onChange={(e) => {
                          e.stopPropagation();
                          setEmployeeSearchQuery(e.target.value);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="h-8"
                      />
                    </div>
                    <div className="max-h-[300px] overflow-y-auto">
                      {employees
                        .filter(emp => {
                          const empName = emp.user_id?.name || emp.name;
                          return empName.toLowerCase().includes(employeeSearchQuery.toLowerCase());
                        })
                        .map(emp => (
                          <SelectItem key={emp._id} value={emp.user_id?.name || emp.name}>
                            {emp.user_id?.name || emp.name}
                          </SelectItem>
                        ))}
                      {employees.filter(emp => {
                        const empName = emp.user_id?.name || emp.name;
                        return empName.toLowerCase().includes(employeeSearchQuery.toLowerCase());
                      }).length === 0 && (
                          <div className="py-6 text-center text-sm text-muted-foreground">
                            No employee found.
                          </div>
                        )}
                    </div>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Priority</Label>
                <Select value={newGoal.priority} onValueChange={(v) => setNewGoal(p => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Critical">Critical</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select value={newGoal.type} onValueChange={(v) => setNewGoal(p => ({ ...p, type: v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OKR">OKR</SelectItem>
                    <SelectItem value="Project">Project</SelectItem>
                    <SelectItem value="Compliance">Compliance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Project</Label>
                {isCreatingProject ? (
                  <div className="flex gap-2">
                    <Input placeholder="New project name..." value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} className="flex-1" data-testid="new-project-input" autoFocus />
                    <Button size="sm" onClick={handleCreateProject} data-testid="confirm-new-project">Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setIsCreatingProject(false)}><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
                    <Select value={newGoal.project} onValueChange={(v) => setNewGoal(p => ({ ...p, project: v }))}>
                      <SelectTrigger className="w-full min-w-0 [&>span]:truncate">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>{projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" className="gap-1 whitespace-nowrap" onClick={() => setIsCreatingProject(true)} data-testid="create-project-btn">
                      <Plus className="h-3 w-3" /> New
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Start Date</Label>
                <DatePickerInput
                  value={newGoal.startDate}
                  onChange={(value) => setNewGoal((p) => ({ ...p, startDate: value }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Due Date</Label>
                <DatePickerInput
                  value={newGoal.dueDate}
                  onChange={(value) => setNewGoal((p) => ({ ...p, dueDate: value }))}
                  minDate={newGoal.startDate || undefined}
                />
              </div>
            </div>
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Key Results *</Label>
                <Button size="sm" variant="ghost" className="text-xs gap-1 text-primary h-7" onClick={addNewKRField} data-testid="add-kr-field">
                  <Plus className="h-3 w-3" /> Add Key Result
                </Button>
              </div>
              {newGoal.keyResults.map((kr, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-4">{i + 1}.</span>
                  <Input placeholder="e.g., Achieve 95% customer satisfaction" value={kr.text} onChange={(e) => updateNewKR(i, e.target.value)} className="flex-1" data-testid={`new-kr-${i}`} />
                  {newGoal.keyResults.length > 1 && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-500" onClick={() => removeNewKR(i)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setNewGoal({
                title: "",
                description: "",
                owner: "",
                priority: "",
                type: "",
                project: "",
                startDate: "",
                dueDate: "",
                keyResults: [{ id: 'new_kr1', text: "", progress: 0 }]
              });
              setIsCreatingProject(false);
              setNewProjectName("");
              setIsCreateOpen(false);
            }}>Cancel</Button>
            <Button onClick={handleCreateGoal} data-testid="submit-create-goal" 
            disabled={!newGoal.title.trim() || !newGoal.owner || !newGoal.priority || 
            !newGoal.type || !newGoal.project || !newGoal.startDate || !newGoal.dueDate ||
             newGoal.keyResults.length === 0}>Create Goal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== UPDATE PROGRESS DIALOG ========== */}
      <Dialog open={isProgressOpen} onOpenChange={setIsProgressOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Update Progress</DialogTitle>
            <DialogDescription>Adjust progress for each key result of "{editingGoal?.title}"</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {editingGoal?.keyResults.map((kr) => (
              <div key={kr.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">{kr.text || kr.title}</Label>
                  <span className="text-sm font-bold text-primary">{progressEdits[kr.id] ?? kr.progress ?? kr.current ?? 0}%</span>
                </div>
                <Slider
                  value={[progressEdits[kr.id] ?? kr.progress ?? kr.current ?? 0]}
                  onValueChange={(val) => setProgressEdits(prev => ({ ...prev, [kr.id]: val[0] }))}
                  max={100} step={5}
                  data-testid={`slider-${kr.id}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsProgressOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveProgress}
              data-testid="save-progress-btn"
              disabled={!editingGoal?.keyResults.some(kr => (progressEdits[kr.id] ?? kr.current ?? kr.progress ?? 0) !== initialProgressEdits[kr.id])}
            >
              <Save className="h-4 w-4 mr-2" /> Save Progress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== EDIT GOAL DIALOG ========== */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Goal</DialogTitle>
          </DialogHeader>
          {editingGoal && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2"><Label>Title</Label><Input value={editingGoal.title} onChange={(e) => setEditingGoal(p => ({ ...p, title: e.target.value }))} /></div>
              <div className="grid gap-2"><Label>Description</Label><Textarea placeholder="Describe the goal and expected outcomes..." value={editingGoal.description} onChange={(e) => setEditingGoal(p => ({ ...p, description: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Priority</Label>
                  <Select value={editingGoal.priority} onValueChange={(v) => setEditingGoal(p => ({ ...p, priority: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Critical">Critical</SelectItem><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Project</Label>
                  <Select value={editingGoal.project} onValueChange={(v) => setEditingGoal(p => ({ ...p, project: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Type</Label>
                  <Select value={editingGoal.type} onValueChange={(v) => setEditingGoal(p => ({ ...p, type: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="OKR">OKR</SelectItem>
                      <SelectItem value="Project">Project</SelectItem>
                      <SelectItem value="Compliance">Compliance</SelectItem>
                      </SelectContent>
                    </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Employee</Label>
                  <Input
                    value={editingGoal.employee_name || editingGoal.owner || ""}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Start Date</Label>
                  <DatePickerInput
                    value={editingGoal.startDate}
                    onChange={(value) => setEditingGoal(p => ({ ...p, startDate: value }))}
                    disabled
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Due Date</Label>
                  <DatePickerInput
                    value={editingGoal.dueDate}
                    onChange={(value) => setEditingGoal(p => ({ ...p, dueDate: value }))}
                    minDate={editingGoal.startDate || undefined}
                  />
                </div>
              </div>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Key Results ({editingGoal.keyResults.length})</Label>
                  <Button size="sm" variant="ghost" className="text-xs gap-1 text-primary h-7" data-testid="edit-add-kr"
                    onClick={() => setEditingGoal(p => ({ ...p, keyResults: [...p.keyResults, { id: 'kr' + Date.now(), text: "", title: "", progress: 0, current: 0 }] }))}>
                    <Plus className="h-3 w-3" /> Add Key Result
                  </Button>
                </div>
                {editingGoal.keyResults.map((kr, i) => (
                  <div key={kr.id} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-4 shrink-0">{i + 1}.</span>
                    <Input value={kr.text || kr.title || ''} className="flex-1"
                      placeholder="Key result description..."
                      onChange={(e) => setEditingGoal(p => ({
                        ...p, keyResults: p.keyResults.map((k, idx) => idx === i ? { ...k, text: e.target.value, title: e.target.value } : k)
                      }))}
                      data-testid={`edit-kr-${i}`}
                    />
                    <div className="flex items-center gap-1 shrink-0 w-20">
                      <Input type="number" min={0} max={100} value={kr.progress ?? kr.current ?? 0} className="h-8 w-14 text-xs text-center px-1 hide-spinner"
                        onChange={(e) => setEditingGoal(p => ({
                          ...p, keyResults: p.keyResults.map((k, idx) => idx === i ? { ...k, progress: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)), current: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) } : k)
                        }))}
                      />
                      <span className="text-xs text-muted-foreground">%</span>
                    </div>
                    {editingGoal.keyResults.length > 1 && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-500 shrink-0"
                        onClick={() => setEditingGoal(p => ({ ...p, keyResults: p.keyResults.filter((_, idx) => idx !== i) }))}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              data-testid="save-edit-btn"
              disabled={JSON.stringify(editingGoal) === JSON.stringify(initialEditGoal)}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DELETE GOAL DIALOG ========== */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Goal</DialogTitle>
            <DialogDescription>Are you sure you want to delete "{editingGoal?.title}"? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== ADD KEY RESULT DIALOG ========== */}
      <Dialog open={isAddKROpen} onOpenChange={setIsAddKROpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Add Key Result</DialogTitle>
            <DialogDescription>Add a new measurable key result to "{editingGoal?.title}"</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Key Result</Label>
            <Input className="mt-2" placeholder="e.g., Complete user research with 50 participants" value={newKRText} onChange={(e) => setNewKRText(e.target.value)} data-testid="new-kr-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddKROpen(false)}>Cancel</Button>
            <Button
              onClick={handleAddKR}
              data-testid="submit-add-kr"
              disabled={!newKRText.trim()}
            >
              Add Key Result
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoalTracking;
